import type { Dispatch, SetStateAction } from 'react';
import { Platform } from 'react-native';
import { Coordinate, TrailPoint } from '@/types/trail';
import { calculateDistance } from './distance';

// Use very permissive thresholds - rely on react-native-background-geolocation's built-in filtering
// These are fallback safeguards only, not active filtering
export const BG_MAX_LOCATION_AGE_MS = 300_000; // 5 minutes - allow delayed Android location updates
export const BG_MAX_ACCURACY_METERS = 500; // Very permissive - let SDK handle accuracy filtering
export const BG_DISTANCE_FILTER_METERS = 0; // No distance filter - record all locations

// ─── Stale/cached location jump detection ──────────────────────────
// On iOS, Core Location re-delivers cached locations (often the initial
// recording position) when the current GPS fix is lost.  These manifest
// as coordinates with poor accuracy that are far from the last good path
// point.  The thresholds below filter these stale fixes while remaining
// permissive enough for Android's legitimately delayed (but HIGH-accuracy)
// location updates.

/** Accuracy (m) above which a location is considered "poor" and subject
 *  to jump-distance filtering.  iOS typically reports <30 m for a good
 *  GPS fix and 65–500 m for degraded/cached fixes.  Android delayed-but-
 *  valid fixes usually report good accuracy (10–30 m) even after a long
 *  gap, so they pass this threshold and are accepted. */
export const JUMP_POOR_ACCURACY_METERS = 65;

/** Maximum distance (m) we'll accept for a poor-accuracy coordinate from
 *  the last path point.  Beyond this the point is treated as a stale
 *  cache re-delivery and rejected. */
export const JUMP_MAX_DISTANCE_METERS = 200;

/** Maximum distance (m) for sample (stationary-monitor) locations.
 *  Samples are cached/stale by definition — any significant jump is
 *  certainly a stale re-delivery. */
export const SAMPLE_MAX_DISTANCE_METERS = 50;

/** Speed (m/s) above which the implied movement between two consecutive
 *  fixes is physically impossible (≈72 km/h) and the point is treated as
 *  a GPS artifact — e.g. a cached fix re-delivered with a fresh timestamp
 *  after a signal dropout.  Fastest real dog ≈ 18 m/s; GPS artifacts are
 *  typically hundreds of metres in ~1 s. */
export const MAX_PLAUSIBLE_SPEED_MPS = 20;

/** Minimum distance (m) for a BACKDATED fix (GPS timestamp older than the
 *  last accepted point) to be rejected.  Core Location re-delivers cached
 *  fixes (often the recording start position) carrying their ORIGINAL
 *  timestamp — a backdated fix that far away is a stale cache, never a
 *  live fix.  Slightly-backdated small jitter (< this) is harmless. */
export const BACKDATED_MAX_DISTANCE_METERS = 25;

type BGLocationLike = {
  sample?: boolean;
  timestamp?: string | number;
  odometer?: number;
  mock?: boolean;
  activity?: { type?: string; confidence?: number };
  battery?: { level?: number; is_charging?: boolean };
  coords?: {
    latitude?: number;
    longitude?: number;
    accuracy?: number | null;
    altitude?: number | null;
    altitude_accuracy?: number | null;
    speed?: number | null;
    heading?: number | null;
  };
};

type ValidateOptions = {
  maxLocationAgeMs?: number;
  maxAccuracyMeters?: number;
  /** Reject any location whose GPS timestamp (ms) is older than this value. */
  minTimestampMs?: number;
  now?: number;
};

export function getLocationTimestampMs(location: BGLocationLike): number | null {
  const raw = location?.timestamp;
  const parsed = typeof raw === 'number' ? raw : new Date(raw || '').getTime();
  return Number.isFinite(parsed) ? parsed : null;
}

export function getLocationAccuracyMeters(location: BGLocationLike): number | undefined {
  const accuracy = Number(location?.coords?.accuracy);
  return Number.isFinite(accuracy) ? accuracy : undefined;
}

export function shouldAcceptTrackedLocation(location: BGLocationLike, options: ValidateOptions = {}): boolean {
  // Minimal validation - rely on react-native-background-geolocation's built-in filtering
  const timestamp = getLocationTimestampMs(location);
  if (!timestamp) {
    console.log('shouldAcceptTrackedLocation: rejected (no timestamp)');
    return false;
  }

  const accuracy = getLocationAccuracyMeters(location);
  if (accuracy == null) {
    console.log('shouldAcceptTrackedLocation: rejected (no accuracy)');
    return false;
  }

  console.log(`shouldAcceptTrackedLocation: accepted timestamp=${new Date(timestamp).toISOString()} accuracy_m=${accuracy}`);
  return true;
}

export function toCoordinate(location: BGLocationLike): Coordinate | null {
  const latitude = Number(location?.coords?.latitude);
  const longitude = Number(location?.coords?.longitude);
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;
  return { latitude, longitude };
}

/** Coerce a possibly-null numeric GPS value to a finite number, else undefined. */
const numOrUndef = (n: unknown): number | undefined => {
  const v = Number(n);
  return Number.isFinite(v) ? v : undefined;
};

/** Build a full {@link TrailPoint} from a `BGLocation` event, preserving
 *  every per-fix field the backend needs for speed/accuracy smoothing.
 *  Invalid sentinels (e.g. iOS `-1` for speed/heading/altitude_accuracy)
 *  are kept as-is so the backend can interpret them. */
export function toTrailPoint(location: BGLocationLike): TrailPoint | null {
  const latitude = Number(location?.coords?.latitude);
  const longitude = Number(location?.coords?.longitude);
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;
  const ts = getLocationTimestampMs(location);
  return {
    latitude,
    longitude,
    ...(ts != null ? { recordedAt: ts } : { recordedAt: Date.now() }),
    accuracy: getLocationAccuracyMeters(location),
    altitude: numOrUndef(location?.coords?.altitude),
    altitudeAccuracy: numOrUndef(location?.coords?.altitude_accuracy),
    speed: numOrUndef(location?.coords?.speed),
    heading: numOrUndef(location?.coords?.heading),
    odometer: numOrUndef(location?.odometer),
    activityType: location?.activity?.type,
    activityConfidence: numOrUndef(location?.activity?.confidence),
    isSample: !!location?.sample,
    isMock: !!location?.mock,
    batteryLevel: numOrUndef(location?.battery?.level),
    batteryCharging: !!location?.battery?.is_charging,
    platform: Platform.OS === 'ios' ? 'ios' : 'android',
  };
}

/** Options for appendCoordinateToPath that enable stale-location filtering. */
export type AppendOptions = {
  /** Horizontal accuracy in metres (from coords.accuracy). */
  accuracy?: number;
  /** True if this is a stationary-sample location (BG location.sample). */
  isSample?: boolean;
};

/**
 * Determine whether a new coordinate is a plausible addition to the path
 * given the last accepted point, its accuracy, and whether it is a sample.
 *
 * This is the core stale-cache filter.  On iOS, when GPS quality degrades
 * Core Location often re-delivers a cached location (commonly the initial
 * recording position).  These stale fixes have TWO distinguishing traits:
 *
 *   1. Poor horizontal accuracy  (>65 m — iOS reports <30 m for good fixes)
 *   2. Large distance from the last good path point
 *
 * Android's legitimately delayed updates differ: they carry GOOD accuracy
 * (10–30 m) even after a long delivery gap, so they pass this check.
 *
 * @returns `true` if the coordinate should be appended, `false` to reject.
 */
export function isPlausiblePathAppend(
  newCoord: Coordinate & { recordedAt?: number },
  lastCoord: (Coordinate & { recordedAt?: number }) | undefined,
  accuracy: number | undefined,
  isSample: boolean,
): boolean {
  if (!lastCoord) return true; // First point always accepted

  const distM = calculateDistance(lastCoord, newCoord) * 1000; // km → m

  // 1) Sample (stationary-monitor) locations with any significant jump
  //    are always stale → reject.
  if (isSample && distM > SAMPLE_MAX_DISTANCE_METERS) {
    console.log(
      `isPlausiblePathAppend: rejected sample jump — ${distM.toFixed(0)}m from last point`,
    );
    return false;
  }

  // 2) Poor-accuracy + large jump = likely stale iOS cache re-delivery.
  //    Android delayed-but-valid fixes typically have GOOD accuracy
  //    (<30 m) even when far from the last point, so they pass this
  //    check and are accepted.
  if (accuracy != null && accuracy > JUMP_POOR_ACCURACY_METERS && distM > JUMP_MAX_DISTANCE_METERS) {
    console.log(
      `isPlausiblePathAppend: rejected stale jump — ${distM.toFixed(0)}m with accuracy ${accuracy.toFixed(0)}m`,
    );
    return false;
  }

  // 3) Implied-speed teleport check.  A fix far from the last point whose
  //    GPS timestamps imply movement faster than any dog/human can be a
  //    dropout artifact (cached fix re-delivered, possibly with a fresh
  //    timestamp).  dt is clamped to ≥1 s to survive equal/backdated
  //    timestamps and integer-ms rounding.
  if (newCoord.recordedAt != null && lastCoord.recordedAt != null) {
    const dtS = Math.max((newCoord.recordedAt - lastCoord.recordedAt) / 1000, 1);
    if (distM / dtS > MAX_PLAUSIBLE_SPEED_MPS) {
      console.log(
        `isPlausiblePathAppend: rejected teleport — ${distM.toFixed(0)}m in ${dtS.toFixed(0)}s ` +
        `(implied ${((distM / dtS) * 3.6).toFixed(0)} km/h)`,
      );
      return false;
    }
  }

  // 4) Backdated cache re-delivery: a fix whose GPS timestamp is OLDER
  //    than the last accepted point but far away from it.  A cached fix
  //    (e.g. the recording start position) carries its original timestamp,
  //    so "older + far" is a stale cache by construction.
  if (
    newCoord.recordedAt != null &&
    lastCoord.recordedAt != null &&
    newCoord.recordedAt <= lastCoord.recordedAt &&
    distM > BACKDATED_MAX_DISTANCE_METERS
  ) {
    console.log(
      `isPlausiblePathAppend: rejected backdated jump — ${distM.toFixed(0)}m at/before last point's timestamp`,
    );
    return false;
  }

  return true;
}

export function appendCoordinateToPath(
  setCoordinates: Dispatch<SetStateAction<TrailPoint[]>>,
  coordinate: TrailPoint,
  prevCoords?: TrailPoint[],
  options?: AppendOptions,
) {
  const accuracy = options?.accuracy ?? coordinate.accuracy;
  const isSample = options?.isSample ?? coordinate.isSample ?? false;

  setCoordinates((prev) => {
    // ── Jump detection: reject stale/cached GPS locations ──────────
    // Prevents iOS stale cache re-deliveries from creating diagonal
    // spikes in the path and corrupting distance calculations.
    const lastCoord = prev.length > 0 ? prev[prev.length - 1] : undefined;
    if (!isPlausiblePathAppend(coordinate, lastCoord, accuracy, isSample)) {
      return prev; // Reject — don't add this point
    }

    return [...prev, coordinate];
  });
}

/**
 * Filter a recorded path to remove jumps (out-of-order timestamps, low accuracy).
 * Pass raw location objects from BackgroundGeolocation.getLocations().
 * @returns Cleaned Coordinate array preserving only valid, monotonic timestamps.
 */
export function cleanRecordedPath(locations: BGLocationLike[]): Coordinate[] {
  const cleaned: Coordinate[] = [];
  let lastGpsTimestamp = 0;

  for (const loc of locations) {
    const coord = toCoordinate(loc);
    if (!coord) continue;

    // Check accuracy
    const accuracy = getLocationAccuracyMeters(loc);
    if (accuracy == null || accuracy > BG_MAX_ACCURACY_METERS) continue;

    // Check timestamp monotonicity
    const gpsTs = getLocationTimestampMs(loc);
    if (!gpsTs || gpsTs <= lastGpsTimestamp) continue;

    lastGpsTimestamp = gpsTs;
    cleaned.push(coord);
  }

  return cleaned;
}
